import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-final-form';
import { ConfirmationModal, FormModal } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { getDirtyFields } from '@ubeya/shared/utils/form';
import { getRichTextVisibleLength } from '@ubeya/shared-web/components/RichTextEditor/markdownUtils';
import SubLocationFields from './SubLocationFields';
import { MAX_LOCATION_ATTACHMENTS, LOCATION_INSTRUCTIONS_MAX_LENGTH } from 'settings/constants';

const LocationModal = styled(FormModal)`
  min-height: 40vh;
  max-height: 80vh;

  .confirmation-modal-wrapper {
    z-index: 10;
  }
`;

const SubLocationModal = ({ selectedSubLocation, setSubLocationsChanged, onClose, handleAdd, handleEdit }) => {
  const { t } = useContext(ConfigContext);
  const [showPromptBeforeUpdate, setShowPromptBeforeUpdate] = useState(false);
  const form = useForm();

  const {
    values: { address, lat, location: parentLocation, lng }
  } = form.getState();

  const initialValues = useMemo(
    () => ({
      ...selectedSubLocation,
      photo: selectedSubLocation?.photo || selectedSubLocation?.image
    }),
    [selectedSubLocation]
  );

  const validate = useCallback(
    (values) => {
      const { instructions = '', attachments = [] } = values;
      const errors = {};

      if (getRichTextVisibleLength(instructions) > LOCATION_INSTRUCTIONS_MAX_LENGTH) {
        errors.instructions = t('tooManyCharactersInInstructionsErrorBody');
      }

      const filteredAttachments = (attachments || []).filter(({ status }) => status !== 'deleted');
      if (filteredAttachments?.length > MAX_LOCATION_ATTACHMENTS) {
        errors.attachments = t('tooManyAttachmentsErrorBody');
      }

      return errors;
    },
    [t]
  );

  const submitSubLocation = ({ values }) => {
    if (selectedSubLocation.id) {
      handleEdit(selectedSubLocation.id, values);
    } else {
      handleAdd(values);
    }
    setSubLocationsChanged();
    onClose();
  };

  const handleSubmit = (values, currForm) => {
    const { dirtyFields } = currForm.getState();
    const dirtyValues = getDirtyFields(values, dirtyFields);

    const parentLocationAddress = { address, lat, lng, ...parentLocation };
    const allValues = { ...parentLocationAddress, ...dirtyValues };

    if (dirtyFields.attachments) {
      allValues.attachments = values.attachments;
    }

    if (selectedSubLocation?.id && Object.entries(dirtyFields)?.length > 0) {
      setShowPromptBeforeUpdate({ values: allValues, form: currForm });
    } else {
      submitSubLocation({ values: allValues, form: currForm });
    }
  };

  return (
    <LocationModal
      initialValues={initialValues}
      onSubmit={handleSubmit}
      size="small"
      validate={validate}
      onClose={onClose}
      title={selectedSubLocation.id ? t('editSubLocation') : t('addSubLocation')}
      confirmText={selectedSubLocation.id ? t('update') : t('add')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnInvalid
      disabledOnPristine={false}>
      <SubLocationFields parentLocation={{ lat, lng, ...parentLocation }} selectedSubLocation={selectedSubLocation} />

      {showPromptBeforeUpdate && (
        <ConfirmationModal
          onClose={() => setShowPromptBeforeUpdate(false)}
          title={t('areYouSure')}
          body={t('areYouSureUpdateLocation')}
          onConfirm={() => {
            submitSubLocation(showPromptBeforeUpdate);
            setShowPromptBeforeUpdate(false);
          }}
          confirmText={t('ok')}
        />
      )}
    </LocationModal>
  );
};

export default SubLocationModal;
